/**
 * Pure lobby rules for the redesigned pre-game lobby (docs/setup-redesign,
 * sections 4 and 5): who the viewer is, what each seat shows and which actions
 * it offers, the start status line, sharing (public games never show a code),
 * the max-players choices and the join request for "Take this seat".
 *
 * Kept free of React so the lobby, the join-by-code screen and the tests share
 * one source of truth; the components only render what these return.
 */

import {
  mapPlayerCapacity,
  mapSeatRange,
  PLAYER_SEATS_MAX,
  type GameResponse,
  type GamePlayerResponse,
  type MapDef,
  type PreviewGameResponse,
  type TeamId,
  type TeamsConfig,
} from '@warcarp/api-types'
import { teamForSlot, teamLabel } from '@warcarp/api-types'
import { computeSeatMap } from './seatMap'

import { initialJoinColor } from './playerColor'
import { lobbySeats } from './gameSetupDraft'

/**
 * host     — the game's admin (always seated in the lobby).
 * member   — a seated player who isn't the host.
 * invited  — holds a pending invite that reserves a seat ("saved for you").
 * visitor  — anyone else looking at the lobby (open public game, or a pending
 *            code invite that reserves nothing): they take an open seat.
 */
export type LobbyRole = 'host' | 'member' | 'invited' | 'visitor'

export type SeatAction = 'colour' | 'remove' | 'resend' | 'revoke' | 'invite' | 'addBot' | 'take' | 'move'

export type LobbySeatRow =
  | { slot: number; kind: 'player'; player: GamePlayerResponse; name: string; you: boolean; meta: string; actions: SeatAction[] }
  | { slot: number; kind: 'invited'; invitationId: string; inviteeId: string; name: string; you: boolean; meta: string; actions: SeatAction[] }
  | { slot: number; kind: 'open'; name: string; you: false; meta: string; actions: SeatAction[] }

type LobbyGame = Pick<GameResponse, 'adminId' | 'players' | 'invitations' | 'maxPlayers' | 'isPublic'>

/** The viewer's pending invitation, if any. */
export function viewerPendingInvite(game: Pick<GameResponse, 'invitations'>, viewerId: string | null | undefined) {
  if (!viewerId) return null
  return game.invitations.find((inv) => inv.status === 'pending' && inv.user.id === viewerId) ?? null
}

export function lobbyRole(game: LobbyGame, viewerId: string | null | undefined): LobbyRole {
  if (viewerId && game.adminId === viewerId) return 'host'
  if (viewerId && game.players.some((p) => p.userId === viewerId)) return 'member'
  const invite = viewerPendingInvite(game, viewerId)
  // A code redemption grants a non-reserving invite (slot null): nothing is
  // saved for them, so they pick an open seat like any visitor.
  if (invite && invite.slot != null && !game.players.some((p) => p.slot === invite.slot)) return 'invited'
  return 'visitor'
}

/** The host's display name, for "Waiting for <host> to start" and the invite banner. */
export function lobbyHostName(game: Pick<GameResponse, 'adminId' | 'players'>): string {
  return game.players.find((p) => p.userId === game.adminId)?.displayName ?? 'the host'
}

/** "bot · Risk manager" style profile line for a bot seat. */
export function botSeatMeta(player: Pick<GamePlayerResponse, 'botPersonality'>): string {
  const personality = player.botPersonality?.replace(/-/g, ' ').trim()
  return personality ? `bot · ${personality.charAt(0).toUpperCase()}${personality.slice(1)}` : 'bot'
}

function openSeatMeta(isPublic: boolean): string {
  return isPublic ? 'open to anyone in the games list' : 'open to anyone with the code or an invite'
}

/**
 * One row per seat (1..maxPlayers), with the actions the viewer may take.
 *
 * Invite and Add bot sit on the FIRST open seat only: the server reserves the
 * lowest open seat for an invite and seats a bot there too. Pick-seats team
 * lobbies are the exception for Add bot: the host chooses the team, so every
 * open seat offers it (add-bot takes that `slot`). Bots join fog, team and
 * team fog lobbies like any other. Visitors get an identical "Take this seat"
 * on every open seat.
 */
export function lobbySeatRows(
  game: LobbyGame,
  opts: {
    viewerId: string | null | undefined
    /**
     * Team lobby format. With Pick seats, the host gets Add bot on every open
     * seat and a seated viewer gets "Move here" on open seats of the other teams.
     */
    teams?: TeamsConfig
  },
): LobbySeatRow[] {
  const role = lobbyRole(game, opts.viewerId)
  const isHost = role === 'host'
  const pickSeats = opts.teams?.assignment === 'manual' ? opts.teams : undefined
  const mySlot = opts.viewerId ? game.players.find((p) => p.userId === opts.viewerId)?.slot : undefined
  const myTeam = pickSeats && mySlot != null ? teamForSlot(mySlot, pickSeats.size) : null
  let firstOpen = true
  return computeSeatMap(game.players, game.invitations, game.maxPlayers).map((seat): LobbySeatRow => {
    if (seat.kind === 'filled') {
      const { player } = seat
      const you = !!opts.viewerId && player.userId === opts.viewerId
      const seatIsHost = player.userId === game.adminId
      const meta = seatIsHost ? 'host' : player.isBot ? botSeatMeta(player) : 'joined'
      const actions: SeatAction[] = you ? ['colour'] : isHost && !seatIsHost ? ['remove'] : []
      return { slot: seat.slot, kind: 'player', player, name: player.displayName, you, meta, actions }
    }
    if (seat.kind === 'reserved') {
      const invite = game.invitations.find((inv) => inv.id === seat.invitationId)
      const inviteeId = invite?.user.id ?? ''
      const you = !!opts.viewerId && inviteeId === opts.viewerId
      return {
        slot: seat.slot, kind: 'invited', invitationId: seat.invitationId, inviteeId, name: seat.inviteeName, you,
        meta: you ? 'saved for you' : 'invited · not joined yet',
        actions: isHost ? ['resend', 'revoke'] : [],
      }
    }
    let actions: SeatAction[] = []
    if (isHost && firstOpen) actions = ['invite', 'addBot']
    else if (isHost && pickSeats) actions = ['addBot']
    else if (role === 'visitor') actions = ['take']
    if (myTeam && teamForSlot(seat.slot, pickSeats!.size) !== myTeam) actions = [...actions, 'move']
    firstOpen = false
    return { slot: seat.slot, kind: 'open', name: 'Open seat', you: false, meta: isHost ? openSeatMeta(game.isPublic) : '', actions }
  })
}

/** Seat rows for the join-by-code "found" state, built from the slim preview. */
export function previewSeatRows(preview: Pick<PreviewGameResponse, 'players' | 'availableSlots' | 'maxPlayers'>): Array<
  | { slot: number; kind: 'player'; name: string; meta: string; colorSlot: number }
  | { slot: number; kind: 'reserved'; name: string; meta: string }
  | { slot: number; kind: 'open'; name: string; meta: string }
> {
  const bySlot = new Map(preview.players.map((p) => [p.slot, p]))
  const open = new Set(preview.availableSlots)
  return Array.from({ length: preview.maxPlayers }, (_, i) => {
    const slot = i + 1
    const player = bySlot.get(slot)
    if (player) {
      return { slot, kind: 'player' as const, name: player.displayName, meta: player.isHost ? 'host' : player.isBot ? 'bot' : '', colorSlot: player.colorSlot ?? slot }
    }
    if (open.has(slot)) return { slot, kind: 'open' as const, name: 'Open seat', meta: '' }
    return { slot, kind: 'reserved' as const, name: 'Reserved', meta: 'saved for an invited player' }
  })
}

export type LobbyStartStatus =
  | { kind: 'auto'; text: string }
  | { kind: 'manual-host' }
  | { kind: 'manual-wait'; text: string }

/**
 * Auto-start games never show a Start button, only one status line; manual
 * games give the host the button and everyone else "Waiting for <host>".
 */
export function lobbyStartStatus(
  game: Pick<GameResponse, 'autoStart' | 'players' | 'maxPlayers' | 'adminId'>,
  isHost: boolean,
): LobbyStartStatus {
  if (game.autoStart) return { kind: 'auto', text: `Starts when all seats are full · ${game.players.length}/${game.maxPlayers}` }
  if (isHost) return { kind: 'manual-host' }
  return { kind: 'manual-wait', text: `Waiting for ${lobbyHostName(game)} to start` }
}

/** One line under the host's Start button explaining what starting now means. */
export function startHint(game: Pick<GameResponse, 'players' | 'invitations' | 'maxPlayers' | 'minPlayers'>): string {
  const seated = game.players.length
  if (seated < game.minPlayers) return `Needs ${game.minPlayers} seated to start.`
  const pending = game.invitations.filter((inv) => inv.status === 'pending')
  if (pending.length === 1) return `${pending[0]!.user.displayName} hasn't joined; starting now drops their invite.`
  if (pending.length > 1) return `${pending.length} invited players haven't joined; starting now drops their invites.`
  if (seated < game.maxPlayers) return `Start any time with ${game.minPlayers} or more seated.`
  return 'Every seat is full.'
}

/**
 * The invite link to share (it carries the code), the code itself for private
 * games (the host shows it beside the button), and a note on who can join.
 */
export function lobbyShareInfo(game: Pick<GameResponse, 'isPublic' | 'inviteCode'>): { link: string | null; code: string | null; note: string } {
  const link = game.inviteCode ? inviteLinkUrl(game.inviteCode) : null
  if (game.isPublic) return { link, code: null, note: 'Public: anyone can join from the games list.' }
  return { link, code: game.inviteCode, note: 'Private: only people you invite or who have the link or code can join.' }
}

/** The join URL anyone can open (or paste); routes through the invite landing. */
export function inviteLinkUrl(code: string): string {
  return `https://warcarp.com/invite/${code}`
}

/**
 * Seat counts the host may pick in the lobby. Mirrors PATCH /:id/settings:
 * never below minPlayers or the highest occupied/reserved seat, never above
 * the map's capacity (fixed boards: their own seat range).
 */
export function maxPlayersChoices(
  game: Pick<GameResponse, 'players' | 'invitations' | 'maxPlayers' | 'minPlayers'>,
  map: Pick<MapDef, 'territories' | 'fixedSetup'> | null | undefined,
): number[] {
  const range = mapSeatRange(map)
  const ceiling = map ? (map.fixedSetup ? range.max : mapPlayerCapacity(map)) : PLAYER_SEATS_MAX
  // Same floor as the setup page (highest taken seat, at least minPlayers),
  // and never below what the map seats.
  const floor = Math.max(range.min, lobbySeats(game).floor)
  const choices: number[] = []
  for (let n = floor; n <= ceiling; n++) choices.push(n)
  return choices
}

/**
 * POST /:id/join body for taking a seat: the chosen slot (omitted for an
 * invitee, whose reserved seat the server assigns) and the viewer's default
 * colour when no one holds it, so joining never needs a colour picker.
 */
export function takeSeatJoinRequest(
  gameId: string,
  slot: number | undefined,
  players: ReadonlyArray<{ colorSlot?: number | null; slot: number }>,
  preferredColorSlot: number | null | undefined,
): { gameId: string; slot?: number; color?: number } {
  const color = initialJoinColor(players, preferredColorSlot)
  return { gameId, ...(slot != null ? { slot } : {}), ...(color != null ? { color } : {}) }
}

export interface LobbyTeamGroup {
  team: TeamId
  label: string
  rows: LobbySeatRow[]
}

/** Pick seats: the seat rows under Team A, Team B, … (contiguous seats, teamForSlot). */
export function lobbyTeamGroups(rows: readonly LobbySeatRow[], teams: TeamsConfig): LobbyTeamGroup[] {
  const groups: LobbyTeamGroup[] = []
  for (const row of rows) {
    const team = teamForSlot(row.slot, teams.size)
    if (!team) continue
    let group = groups.find((g) => g.team === team)
    if (!group) { group = { team, label: teamLabel(team), rows: [] }; groups.push(group) }
    group.rows.push(row)
  }
  return groups
}
